import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Label } from "../../components/ui/label";
import { Input } from "../../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "../../components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "../../components/ui/popover";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../../components/ui/dialog";
import { Checkbox } from "../../components/ui/checkbox";
import { toast } from "sonner";
import { 
  LayoutDashboard, Loader2, User, Calendar, Check, ChevronsUpDown, 
  ClipboardCheck, Clock, AlertTriangle, Mail, TrendingUp, CalendarX,
  ArrowLeft, ChevronRight, Download, Send, ExternalLink, MessageSquare
} from "lucide-react";
import { cn } from "../../lib/utils";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
axios.defaults.withCredentials = true;

const DATE_RANGES = [
  { value: "this_month", label: "This Month" },
  { value: "last_month", label: "Last Month" },
  { value: "ytd", label: "Year to Date (YTD)" },
];

// Stat Card Component
const StatCard = ({ icon: Icon, label, value, subValue, color = "blue", className }) => {
  const colorClasses = {
    blue: "bg-blue-50 text-blue-600 border-blue-200",
    green: "bg-green-50 text-green-600 border-green-200",
    red: "bg-red-50 text-red-600 border-red-200",
    orange: "bg-orange-50 text-orange-600 border-orange-200",
    purple: "bg-purple-50 text-purple-600 border-purple-200",
    slate: "bg-slate-50 text-slate-600 border-slate-200",
  };

  return (
    <Card className={cn("border", className)}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div className={cn("p-2 rounded-lg", colorClasses[color])}>
            <Icon className="h-5 w-5" />
          </div>
          {subValue && (
            <span className={cn("text-xs font-medium px-2 py-1 rounded-full", colorClasses[color])}>
              {subValue}
            </span>
          )}
        </div>
        <div className="mt-3">
          <p className="text-2xl font-bold text-slate-900">{value}</p>
          <p className="text-sm text-slate-500">{label}</p>
        </div>
      </CardContent>
    </Card>
  );
};

// Progress Bar Component
const ProgressBar = ({ label, value, color = "blue" }) => {
  const colorClasses = {
    blue: "bg-blue-500",
    green: "bg-green-500",
    red: "bg-red-500",
    orange: "bg-orange-500",
  };

  return (
    <div className="space-y-1">
      <div className="flex justify-between text-sm">
        <span className="text-slate-600">{label}</span>
        <span className="font-semibold text-slate-900">{value}%</span>
      </div>
      <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
        <div 
          className={cn("h-full rounded-full transition-all duration-500", colorClasses[color])}
          style={{ width: `${Math.min(value, 100)}%` }}
        />
      </div>
    </div>
  );
};

export default function UserDashboardPage() {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState("");
  const [selectedRange, setSelectedRange] = useState("this_month");
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [userSearchOpen, setUserSearchOpen] = useState(false);
  
  // Dashboard data state
  const [dashboardData, setDashboardData] = useState(null);
  const [showDashboard, setShowDashboard] = useState(false);
  
  // Email dialog state
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [ccTeamLeader, setCcTeamLeader] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [sendingTestEmail, setSendingTestEmail] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [usersRes, teamsRes] = await Promise.all([
        axios.get(`${API}/users`),
        axios.get(`${API}/teams`)
      ]);
      const activeUsers = usersRes.data.filter(u => u.is_active && !u.is_exempt);
      // Sort alphabetically by full_name
      activeUsers.sort((a, b) => (a.full_name || '').localeCompare(b.full_name || ''));
      setUsers(activeUsers);
      setTeams(teamsRes.data);
    } catch (err) {
      console.error("Error fetching data:", err);
      toast.error("Failed to load users");
    } finally {
      setLoading(false);
    }
  };

  const getTeamNames = (user) => {
    if (!user?.team_ids || user.team_ids.length === 0) return "No Team";
    return user.team_ids
      .map(tid => teams.find(t => t.id === tid)?.name)
      .filter(Boolean)
      .join(", ") || "No Team";
  };

  const selectedUser = users.find(u => u.id === selectedUserId);

  const handleGenerateDashboard = async () => {
    if (!selectedUserId) {
      toast.error("Please select a user");
      return;
    }

    setGenerating(true);
    try {
      const response = await axios.post(`${API}/admin/user-dashboard`, {
        user_id: selectedUserId,
        date_range: selectedRange
      });
      
      setDashboardData(response.data);
      setShowDashboard(true);
      toast.success("Dashboard generated!");
    } catch (err) {
      console.error("Error generating dashboard:", err);
      toast.error(err.response?.data?.detail || "Failed to generate dashboard");
    } finally {
      setGenerating(false);
    }
  };

  const handleDownloadPDF = async () => {
    try {
      const response = await axios.post(
        `${API}/admin/user-dashboard/pdf`,
        { user_id: selectedUserId, date_range: selectedRange },
        { responseType: 'blob', timeout: 60000 }
      );
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${dashboardData?.user?.name?.replace(/\s+/g, '_') || 'user'}_dashboard_${selectedRange}.pdf`;
      link.click();
      URL.revokeObjectURL(url);
      toast.success("PDF downloaded!");
    } catch (err) {
      toast.error("Failed to download PDF");
    }
  };

  const handleTestEmail = async () => {
    setSendingTestEmail(true);
    try {
      const response = await axios.post(`${API}/admin/user-dashboard/test-email`, {
        user_id: selectedUserId,
        date_range: selectedRange
      });
      
      toast.success(response.data.message);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send test email");
    } finally {
      setSendingTestEmail(false);
    }
  };

  const handleSendEmail = async () => {
    setSendingEmail(true);
    try {
      const response = await axios.post(`${API}/admin/user-dashboard/send-email`, {
        user_id: selectedUserId,
        date_range: selectedRange,
        cc_team_leader: ccTeamLeader
      });
      
      toast.success(response.data.message);
      setEmailDialogOpen(false);
      setCcTeamLeader(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send email");
    } finally {
      setSendingEmail(false);
    }
  };

  const handleMissingDateClick = (dateRaw) => {
    // Navigate to daily log page with the date
    navigate(`/daily-log?date=${dateRaw}`);
  };

  const handleBack = () => {
    setShowDashboard(false);
    setDashboardData(null);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  // Dashboard View
  if (showDashboard && dashboardData) {
    const stats = dashboardData.stats;
    const missingDatesRaw = dashboardData.missing_dates_raw || [];
    
    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={handleBack}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-slate-900">User Dashboard</h1>
              <p className="text-slate-500 text-sm">Performance metrics and log statistics</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={handleDownloadPDF} data-testid="download-pdf-btn">
              <Download className="h-4 w-4 mr-2" />
              PDF
            </Button>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={handleTestEmail} 
              disabled={sendingTestEmail}
              data-testid="test-email-btn"
            >
              {sendingTestEmail ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Mail className="h-4 w-4 mr-2" />
              )}
              Test Email
            </Button>
            <Button size="sm" onClick={() => setEmailDialogOpen(true)} data-testid="send-email-btn">
              <Send className="h-4 w-4 mr-2" />
              Send to User
            </Button>
          </div>
        </div>

        {/* Info Banner */}
        <Card className="bg-gradient-to-r from-blue-600 to-blue-800 text-white border-0">
          <CardContent className="p-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              <div>
                <p className="text-blue-200 text-sm">User</p>
                <p className="text-xl font-bold">{dashboardData.user.name}</p>
                <p className="text-blue-200 text-xs">{dashboardData.user.email}</p>
              </div>
              <div>
                <p className="text-blue-200 text-sm">Team</p>
                <p className="text-xl font-bold">{dashboardData.team_name}</p>
              </div>
              <div>
                <p className="text-blue-200 text-sm">Team Leader</p>
                <p className="text-xl font-bold">{dashboardData.team_leader_name}</p>
              </div>
              <div>
                <p className="text-blue-200 text-sm">Period</p>
                <p className="text-xl font-bold">{dashboardData.date_range.label}</p>
                <p className="text-blue-200 text-xs">{dashboardData.date_range.start} - {dashboardData.date_range.end}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Main Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          <StatCard 
            icon={Calendar} 
            label="Working Days" 
            value={stats.working_days}
            color="slate"
          />
          <StatCard 
            icon={ClipboardCheck} 
            label="Logs Submitted" 
            value={stats.logs_submitted}
            subValue={`${stats.completion_percentage}%`}
            color="blue"
          />
          <StatCard 
            icon={Clock} 
            label="Before Deadline" 
            value={stats.logs_before_deadline}
            subValue={`${stats.on_time_percentage}%`}
            color="green"
          />
          <StatCard 
            icon={AlertTriangle} 
            label="After Deadline" 
            value={stats.logs_after_deadline}
            subValue={`${stats.delayed_percentage}%`}
            color="red"
          />
          <StatCard 
            icon={Mail} 
            label="Reminders Sent" 
            value={stats.reminder_emails_sent}
            color="orange"
          />
          <StatCard 
            icon={CalendarX} 
            label="Missing Logs" 
            value={stats.missing_logs}
            color={stats.missing_logs > 0 ? "red" : "green"}
          />
        </div>

        {/* Progress Section */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <TrendingUp className="h-5 w-5 text-primary" />
                Performance Metrics
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <ProgressBar 
                label="Logs Completed" 
                value={stats.completion_percentage} 
                color={stats.completion_percentage >= 80 ? "green" : stats.completion_percentage >= 50 ? "orange" : "red"}
              />
              <ProgressBar 
                label="On Time Submissions" 
                value={stats.on_time_percentage} 
                color={stats.on_time_percentage >= 80 ? "green" : stats.on_time_percentage >= 50 ? "orange" : "red"}
              />
              <ProgressBar 
                label="Delayed Submissions" 
                value={stats.delayed_percentage} 
                color={stats.delayed_percentage <= 20 ? "green" : stats.delayed_percentage <= 50 ? "orange" : "red"}
              />
              
              {/* Average Stats */}
              <div className="pt-4 mt-4 border-t space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-slate-600 text-sm">Avg. Words per Log</span>
                  <span className="font-semibold text-slate-900">{stats.avg_words_per_log || 0}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-slate-600 text-sm">Avg. Characters per Log</span>
                  <span className="font-semibold text-slate-900">{stats.avg_chars_per_log || 0}</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Summary Card */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Summary</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex justify-between py-2 border-b">
                  <span className="text-slate-600">Total Working Days</span>
                  <span className="font-semibold">{stats.working_days}</span>
                </div>
                <div className="flex justify-between py-2 border-b">
                  <span className="text-slate-600">Logs Submitted</span>
                  <span className="font-semibold text-blue-600">{stats.logs_submitted}</span>
                </div>
                <div className="flex justify-between py-2 border-b">
                  <span className="text-slate-600">Before 6 PM Deadline</span>
                  <span className="font-semibold text-green-600">{stats.logs_before_deadline}</span>
                </div>
                <div className="flex justify-between py-2 border-b">
                  <span className="text-slate-600">After Deadline</span>
                  <span className="font-semibold text-red-600">{stats.logs_after_deadline}</span>
                </div>
                <div className="flex justify-between py-2 border-b">
                  <span className="text-slate-600">Reminder Emails</span>
                  <span className="font-semibold text-orange-600">{stats.reminder_emails_sent}</span>
                </div>
                <div className="flex justify-between py-2">
                  <span className="text-slate-600">Missing Logs</span>
                  <span className={cn("font-semibold", stats.missing_logs > 0 ? "text-red-600" : "text-green-600")}>
                    {stats.missing_logs}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Missing Logs Section with Clickable Links */}
        {dashboardData.missing_dates && dashboardData.missing_dates.length > 0 && (
          <Card className="border-red-200">
            <CardHeader className="bg-red-50">
              <CardTitle className="text-lg flex items-center gap-2 text-red-700">
                <CalendarX className="h-5 w-5" />
                Missing Log Dates ({dashboardData.missing_dates.length})
              </CardTitle>
              <CardDescription className="text-red-600">
                Click on any date below to submit the log for that day
              </CardDescription>
            </CardHeader>
            <CardContent className="p-4">
              <div className="flex flex-wrap gap-2">
                {dashboardData.missing_dates.map((date, idx) => (
                  <button
                    key={idx}
                    onClick={() => handleMissingDateClick(missingDatesRaw[idx])}
                    className="px-3 py-2 bg-red-50 text-red-700 rounded-lg text-sm font-medium border border-red-200 hover:bg-red-100 hover:border-red-300 transition-colors flex items-center gap-2 group"
                    data-testid={`missing-date-${missingDatesRaw[idx]}`}
                  >
                    {date}
                    <ExternalLink className="h-3 w-3 opacity-50 group-hover:opacity-100" />
                  </button>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* All Good Message */}
        {dashboardData.missing_dates && dashboardData.missing_dates.length === 0 && (
          <Card className="border-green-200 bg-green-50">
            <CardContent className="p-6 flex items-center gap-4">
              <div className="p-3 bg-green-100 rounded-full">
                <Check className="h-6 w-6 text-green-600" />
              </div>
              <div>
                <p className="font-semibold text-green-800">All Logs Submitted!</p>
                <p className="text-green-600 text-sm">This user has submitted logs for all working days in the selected period.</p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Challenges Summary */}
        {dashboardData.challenges_summary && (
          <Card className="border-amber-200">
            <CardHeader className="bg-amber-50 pb-3">
              <CardTitle className="text-lg flex items-center gap-2 text-amber-800">
                <MessageSquare className="h-5 w-5" />
                Challenges & Hurdles Summary
              </CardTitle>
              <CardDescription className="text-amber-600">
                Key challenges reported during the selected period
              </CardDescription>
            </CardHeader>
            <CardContent className="p-4">
              <p className="text-slate-700 text-sm leading-relaxed whitespace-pre-wrap">
                {dashboardData.challenges_summary}
              </p>
            </CardContent>
          </Card>
        )}

        {/* No Challenges Message */}
        {!dashboardData.challenges_summary && stats.logs_submitted > 0 && (
          <Card className="border-slate-200 bg-slate-50">
            <CardContent className="p-4 flex items-center gap-3">
              <MessageSquare className="h-5 w-5 text-slate-400" />
              <p className="text-slate-500 text-sm">No significant challenges reported during this period.</p>
            </CardContent>
          </Card>
        )}

        {/* Footer */}
        <div className="text-center text-sm text-slate-500">
          Dashboard generated on {dashboardData.generated_at}
        </div>

        {/* Send Email Dialog */}
        <Dialog open={emailDialogOpen} onOpenChange={setEmailDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Mail className="h-5 w-5 text-primary" />
                Send Dashboard to User
              </DialogTitle>
              <DialogDescription>
                Send this performance dashboard to {dashboardData.user.name} via email
              </DialogDescription>
            </DialogHeader>
            <div className="py-4 space-y-4">
              <div className="bg-slate-50 rounded-lg p-4 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">To:</span>
                  <span className="font-medium">{dashboardData.user.email}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">Subject:</span>
                  <span className="font-medium">Your Performance Dashboard - {dashboardData.date_range.label}</span>
                </div>
              </div>
              
              <div className="flex items-center gap-3 p-3 rounded-lg bg-blue-50 border border-blue-200">
                <Checkbox
                  id="cc-leader"
                  checked={ccTeamLeader}
                  onCheckedChange={setCcTeamLeader}
                />
                <Label htmlFor="cc-leader" className="cursor-pointer">
                  <span className="font-medium text-blue-900">CC Team Leader</span>
                  <p className="text-xs text-blue-600">{dashboardData.team_leader_name}</p>
                </Label>
              </div>

              <div className="text-sm text-slate-600">
                <p className="font-medium mb-2">Email will include:</p>
                <ul className="list-disc list-inside space-y-1 text-slate-500">
                  <li>Performance summary with all statistics</li>
                  <li>Clickable links to submit missing logs</li>
                  <li>PDF dashboard as attachment</li>
                </ul>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setEmailDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleSendEmail} disabled={sendingEmail} data-testid="confirm-send-btn">
                {sendingEmail ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Send className="h-4 w-4 mr-2" />
                    Send Email
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // Selection Form
  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-slate-900">User Dashboard</h1>
        <p className="text-slate-500 mt-1">View performance metrics and statistics for individual users</p>
      </div>

      <Card className="max-w-2xl">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <LayoutDashboard className="h-5 w-5 text-primary" />
            Generate User Dashboard
          </CardTitle>
          <CardDescription>
            Select a user and time period to view their log submission statistics and performance metrics.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* User Selection */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <User className="h-4 w-4" />
              Select User
            </Label>
            <Popover open={userSearchOpen} onOpenChange={setUserSearchOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  role="combobox"
                  aria-expanded={userSearchOpen}
                  className="w-full justify-between h-auto min-h-[44px] py-2"
                  data-testid="user-select-trigger"
                >
                  {selectedUser ? (
                    <div className="flex flex-col items-start">
                      <span className="font-medium">{selectedUser.full_name}</span>
                      <span className="text-xs text-slate-500">{selectedUser.email} • {getTeamNames(selectedUser)}</span>
                    </div>
                  ) : (
                    <span className="text-slate-500">Search and select a user...</span>
                  )}
                  <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[400px] p-0" align="start">
                <Command>
                  <CommandInput placeholder="Search users by name or email..." />
                  <CommandList>
                    <CommandEmpty>No user found.</CommandEmpty>
                    <CommandGroup>
                      {users.map((user) => (
                        <CommandItem
                          key={user.id}
                          value={`${user.full_name} ${user.email}`}
                          onSelect={() => {
                            setSelectedUserId(user.id);
                            setUserSearchOpen(false);
                          }}
                          className="flex items-center justify-between py-3"
                        >
                          <div className="flex flex-col">
                            <span className="font-medium">{user.full_name}</span>
                            <span className="text-xs text-slate-500">{user.email}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-slate-400">{getTeamNames(user)}</span>
                            <Check
                              className={cn(
                                "h-4 w-4",
                                selectedUserId === user.id ? "opacity-100" : "opacity-0"
                              )}
                            />
                          </div>
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          </div>

          {/* Date Range Selection */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <Calendar className="h-4 w-4" />
              Time Period
            </Label>
            <Select value={selectedRange} onValueChange={setSelectedRange}>
              <SelectTrigger className="w-full" data-testid="date-range-select">
                <SelectValue placeholder="Select time period" />
              </SelectTrigger>
              <SelectContent>
                {DATE_RANGES.map((range) => (
                  <SelectItem key={range.value} value={range.value}>
                    {range.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Selected Summary */}
          {selectedUser && (
            <div className="bg-slate-50 rounded-lg p-4 border">
              <h4 className="font-medium text-slate-900 mb-2">Selection Summary</h4>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-slate-500">User:</span>
                  <p className="font-medium">{selectedUser.full_name}</p>
                </div>
                <div>
                  <span className="text-slate-500">Team:</span>
                  <p className="font-medium">{getTeamNames(selectedUser)}</p>
                </div>
                <div>
                  <span className="text-slate-500">Email:</span>
                  <p className="font-medium">{selectedUser.email}</p>
                </div>
                <div>
                  <span className="text-slate-500">Period:</span>
                  <p className="font-medium">{DATE_RANGES.find(r => r.value === selectedRange)?.label}</p>
                </div>
              </div>
            </div>
          )}

          {/* Generate Button */}
          <Button
            onClick={handleGenerateDashboard}
            disabled={!selectedUserId || generating}
            className="w-full"
            size="lg"
            data-testid="generate-dashboard-btn"
          >
            {generating ? (
              <>
                <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                Generating Dashboard...
              </>
            ) : (
              <>
                <LayoutDashboard className="mr-2 h-5 w-5" />
                View Dashboard
                <ChevronRight className="ml-2 h-4 w-4" />
              </>
            )}
          </Button>

          <p className="text-xs text-slate-500 text-center">
            Dashboard shows: Working days, logs submitted, on-time vs delayed submissions, 
            reminder emails sent, and missing log dates.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
